import { useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { PlayerContext } from './playerContext';

const PLAYER_STORAGE_KEY = 'bajantube_music_player_state_v1';

const EMPTY_PLAYER_STATE = {
    isOpen: false,
    isPlaying: false,

    type: null,
    playlistTitle: '',
    playlistSubtitle: '',

    currentIndex: 0,
    tracks: [],

    listenId: null,
    listenToken: null,
    listenCompleted: false,
    maxPercent: 0,
    playbackId: 0,
};

function getPersistableState(state) {
    return {
        isOpen: state.isOpen,
        // Do not force autoplay after a reload. Browsers may block it.
        isPlaying: false,

        type: state.type,
        playlistTitle: state.playlistTitle,
        playlistSubtitle: state.playlistSubtitle,

        currentIndex: state.currentIndex,
        tracks: state.tracks,

        listenId: null,
        listenToken: null,
        listenCompleted: false,
        maxPercent: 0,
        playbackId: state.playbackId,
    };
}

function loadInitialPlayerState() {
    if (typeof window === 'undefined') {
        return EMPTY_PLAYER_STATE;
    }

    try {
        const saved = window.localStorage.getItem(PLAYER_STORAGE_KEY);

        if (!saved) {
            return EMPTY_PLAYER_STATE;
        }

        const parsed = JSON.parse(saved);

        if (!parsed?.tracks?.length) {
            return EMPTY_PLAYER_STATE;
        }

        return {
            ...EMPTY_PLAYER_STATE,
            ...parsed,
            isPlaying: false,
            listenId: null,
            listenToken: null,
            listenCompleted: false,
            maxPercent: 0,
            playbackId: Number(parsed.playbackId || 0),
        };
    } catch (error) {
        return EMPTY_PLAYER_STATE;
    }
}

export default function PlayerProvider({ children }) {
    const audioRef = useRef(null);
    const [state, setState] = useState(loadInitialPlayerState);

    const currentTrack = state.tracks[state.currentIndex] || null;
    const hasQueue = state.tracks.length > 0;
    const hasNext = state.currentIndex < state.tracks.length - 1;
    const hasPrevious = state.currentIndex > 0;

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (!state.isOpen || !state.tracks.length) {
                window.localStorage.removeItem(PLAYER_STORAGE_KEY);
                return;
            }

            window.localStorage.setItem(
                PLAYER_STORAGE_KEY,
                JSON.stringify(getPersistableState(state))
            );
        } catch (error) {
            // Ignore storage failures.
        }
    }, [state]);

    function playTrack(track) {
        setState({
            ...EMPTY_PLAYER_STATE,
            isOpen: true,
            isPlaying: true,
            type: 'track',
            tracks: [track],
            playbackId: Date.now(),
        });
    }

    function playPlaylist({ title, subtitle = '', tracks = [], startIndex = 0 }) {
        if (!tracks.length) {
            return;
        }

        setState({
            ...EMPTY_PLAYER_STATE,
            isOpen: true,
            isPlaying: true,
            type: 'playlist',
            playlistTitle: title,
            playlistSubtitle: subtitle,
            currentIndex: startIndex,
            tracks,
            playbackId: Date.now(),
        });
    }

    function togglePlay() {
        setState((current) => ({
            ...current,
            isPlaying: !current.isPlaying,
        }));
    }

    function pause() {
        setState((current) => ({
            ...current,
            isPlaying: false,
        }));
    }

    function play() {
        if (!hasQueue) {
            return;
        }

        setState((current) => ({
            ...current,
            isPlaying: true,
        }));
    }

    function next() {
        setState((current) => {
            const nextIndex = current.currentIndex + 1;

            if (nextIndex >= current.tracks.length) {
                return {
                    ...current,
                    isPlaying: false,
                };
            }

            return {
                ...current,
                currentIndex: nextIndex,
                isPlaying: true,
                listenId: null,
                listenToken: null,
                listenCompleted: false,
                maxPercent: 0,
                playbackId: current.playbackId + 1,
            };
        });
    }

    function previous() {
        setState((current) => {
            const previousIndex = current.currentIndex - 1;

            if (previousIndex < 0) {
                return current;
            }

            return {
                ...current,
                currentIndex: previousIndex,
                isPlaying: true,
                listenId: null,
                listenToken: null,
                listenCompleted: false,
                maxPercent: 0,
                playbackId: current.playbackId + 1,
            };
        });
    }

    function closePlayer() {
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.removeItem(PLAYER_STORAGE_KEY);
                window.localStorage.removeItem('bajantube_music_player_position_v1');
            } catch (error) {
                // Ignore storage failures.
            }
        }

        setState(EMPTY_PLAYER_STATE);
    }

    function updateListenSession(updates) {
        setState((current) => ({
            ...current,
            ...updates,
        }));
    }

    function resetListenSession() {
        setState((current) => ({
            ...current,
            listenId: null,
            listenToken: null,
            listenCompleted: false,
            maxPercent: 0,
        }));
    }

    function playGeneratedQueue({
        clickedTrack,
        trackOfTheDay = null,
        tracks = [],
    }) {
        const queue = [];

        queue.push(clickedTrack);

        if (
            trackOfTheDay &&
            trackOfTheDay.id !== clickedTrack.id
        ) {
            queue.push(trackOfTheDay);
        }

        tracks.forEach((track) => {
            if (
                track.id !== clickedTrack.id &&
                !queue.some((item) => item.id === track.id)
            ) {
                queue.push(track);
            }
        });

        playPlaylist({
            title: 'Up Next',
            subtitle: 'Generated Queue',
            tracks: queue,
        });
    }

    function addToQueue(track) {
        setState((current) => ({
            ...current,
            isOpen: true,
            tracks: [...current.tracks, track],
        }));
    }

    function removeFromQueue(index) {
        setState((current) => {
            const tracks = current.tracks.filter((_, i) => i !== index);

            return {
                ...current,
                tracks,
                currentIndex:
                    index < current.currentIndex
                        ? current.currentIndex - 1
                        : current.currentIndex,
            };
        });
    }

    function playQueueIndex(index) {
        setState((current) => ({
            ...current,
            currentIndex: index,
            isPlaying: true,
            listenId: null,
            listenToken: null,
            listenCompleted: false,
            maxPercent: 0,
            playbackId: current.playbackId + 1,
        }));
    }

    const value = useMemo(
        () => ({
            ...state,

            audioRef,

            currentTrack,
            hasQueue,
            hasNext,
            hasPrevious,

            playTrack,
            playPlaylist,
            addToQueue,
            removeFromQueue,
            playQueueIndex,

            togglePlay,
            pause,
            play,

            next,
            previous,

            closePlayer,

            updateListenSession,
            resetListenSession,
            playGeneratedQueue
        }),
        [state, currentTrack, hasQueue, hasNext, hasPrevious]
    );

    return (
        <PlayerContext.Provider value={value}>
            {children}
        </PlayerContext.Provider>
    );
}
